import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import api, { getMe, getUser, getRanch, refreshAuth, logout as logoutApi, clearLocalSession } from '../services/api'
import { replayQueue } from '../services/offlineQueue'

// M3 pragmatic: backend access-token TTL is 15 minutes. Refresh every
// 10 minutes while the user is authenticated so an active session never
// expires mid-task; a 5-minute safety margin covers a missed tick due to
// backgrounded tabs. A hard revocation or closed laptop still logs the
// user out naturally via axios's 401 → /login redirect.
//
// The interval is env-configurable (via VITE_TOKEN_REFRESH_INTERVAL_MS)
// so the Playwright e2e suite can exercise the loop on a sub-second tick
// without fake-clock timers — hardcoded production default is 10 minutes.
const TOKEN_REFRESH_INTERVAL_MS = (() => {
  const raw = import.meta.env?.VITE_TOKEN_REFRESH_INTERVAL_MS
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 10 * 60 * 1000
})()

const AuthContext = createContext(null)

// Legacy role-permission mapping — kept for back-compat with code that reads
// `permissions` off the user object. New code should go through
// lib/permissions.js (canSeeTab / tabsForRole) instead.
// TODO(Session 3): retire this dict once approval workflows wire through
// the Capability enum.
const ROLE_PERMISSIONS = {
  owner: ['read', 'approve_finance', 'view_reports'],
  manager: [
    'read', 'write', 'approve_events', 'manage_users', 'manage_ranch',
    'record_events', 'view_reports',
  ],
  viewer: ['read', 'view_reports'],
  herdsman: ['read', 'record_events'],
  bush_clearer: ['read', 'perform_lantana_clearing'],
  biochar_operator: ['read', 'perform_biochar'],
  land_monitor: ['read', 'write_land_monitoring'],
  assurance_officer: ['read', 'view_reports'],
  system_admin: [
    'read', 'write', 'delete', 'manage_users', 'manage_ranch',
    'record_events', 'approve_events', 'view_reports',
    'generate_reports', 'system_admin',
  ],
}

// Hierarchy for generic "role_at_least(X)" comparisons. Narrow-scope roles
// (bush_clearer, biochar_operator, land_monitor) sit outside the hierarchy
// since their access is orthogonal, not a level.
const ROLE_HIERARCHY = ['herdsman', 'viewer', 'manager', 'owner', 'system_admin']

export function AuthProvider({ children, ranchId }) {
  // Identity is now discovered from the server: the JWT lives in an HttpOnly
  // cookie that JS cannot read. /auth/me round-trips it to learn who we are.
  const {
    data: me,
    isLoading: meLoading,
    isError: meError,
    error: meErrorObj,
  } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    retry: false,
    staleTime: 1000 * 60 * 5,
  })

  // Remember the last-known identity so a logged-in user can boot and keep
  // working OFFLINE: /auth/me can't be reached with no signal, and without this
  // the app bounces to /login on every offline reload. A real auth failure
  // (401/403 — the error carries a response) clears the cache, so a genuinely
  // logged-out or expired session is never resurrected from it.
  useEffect(() => {
    try {
      if (me) localStorage.setItem('wb_last_me', JSON.stringify(me))
      else if (meErrorObj?.response) localStorage.removeItem('wb_last_me')
    } catch { /* localStorage unavailable (private mode) — best effort */ }
  }, [me, meErrorObj])

  // Offline fallback: if /auth/me failed with a NETWORK error (no response —
  // i.e. we're offline), reuse the cached identity so the app stays usable. A
  // 401/403 (has a response) does NOT fall back — that's a real logout.
  const offlineMe = useMemo(() => {
    const isNetworkError = meError && !meErrorObj?.response
    if (me || !isNetworkError) return null
    try {
      return JSON.parse(localStorage.getItem('wb_last_me') || 'null')
    } catch {
      return null
    }
  }, [me, meError, meErrorObj])

  const effectiveMe = me || offlineMe
  const userId = effectiveMe?.id ?? null

  // Sliding session: while authenticated, rotate the HttpOnly cookie
  // every TOKEN_REFRESH_INTERVAL_MS. Swallow errors — a 401 will be
  // handled by the axios interceptor's redirect-to-login. The handle and
  // every refresh still on the wire live in refs so logout() can stop the
  // loop and wait for rotations that are already out (more than one when a
  // response takes longer than a tick).
  const intervalRef = useRef(null)
  const inflightRefreshesRef = useRef(new Set())
  const loggingOutRef = useRef(false)
  useEffect(() => {
    if (!userId) return
    const tick = () => {
      if (loggingOutRef.current) return
      const p = refreshAuth().catch(() => { /* axios interceptor handles 401 */ })
      inflightRefreshesRef.current.add(p)
      p.finally(() => inflightRefreshesRef.current.delete(p))
    }
    intervalRef.current = setInterval(tick, TOKEN_REFRESH_INTERVAL_MS)
    return () => {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [userId])

  // Logout, in the order that makes it stick: stop the loop, let every refresh
  // already in flight settle (its Set-Cookie would otherwise land AFTER the
  // server cleared the cookie and re-seat a valid session: the race behind the
  // old e2e M3 flake), clear the cookie, forget the cached identity, then reload
  // on /login, which ends every timer and query with the document (AuthProvider
  // also wraps /login, so a client-side route change would not). Each wait is
  // bounded: on a half-open field connection Sign Out still signs out locally.
  const logout = useCallback(async () => {
    loggingOutRef.current = true
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    const bounded = (p, ms) => Promise.race([p, new Promise((resolve) => setTimeout(resolve, ms))])
    await bounded(Promise.allSettled([...inflightRefreshesRef.current]), 3_000)
    try { await bounded(logoutApi(), 5_000) } catch { /* idempotent; a 401 here means already logged out */ }
    clearLocalSession()
    window.location.href = '/login'
  }, [])

  // Flush writes queued while offline once we (re)gain an identity. This covers
  // re-login after a cookie expired mid-offline: the online-event + startup
  // listener (main.jsx) can miss it when the login page client-navigates back
  // into the app without a full reload. Guarded by replayInFlight; no-ops when
  // offline or the queue is empty.
  useEffect(() => {
    if (userId) replayQueue(api).catch(() => { /* stays pending for next sync */ })
  }, [userId])

  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser', userId, ranchId],
    queryFn: () => getUser(userId, ranchId),
    enabled: !!ranchId && !!userId,
  })

  // Ranch config — source of truth for the ranch timezone, which H29 uses
  // to render all user-facing dates. Shared cache key `['ranch', ranchId]`
  // dedupes with Layout.jsx / PaddockForm.jsx / PaddockReconfigure.jsx, so
  // all four consumers get served from one network call.
  const { data: currentRanch } = useQuery({
    queryKey: ['ranch', ranchId],
    queryFn: () => getRanch(ranchId),
    enabled: !!ranchId,
    staleTime: 1000 * 60 * 5,
  })

  const auth = useMemo(() => {
    // Platform role (core.users.role, carried on the JWT) is checked first:
    // a system admin has no ranch_users row, so role_at_ranch is null and
    // the UI must fall back to the /me payload to see the system_admin tab set.
    // Legacy 'admin' is canonicalised to 'system_admin' to match canSeeTab().
    const platformRole = effectiveMe?.role
    const isAdmin = platformRole === 'system_admin' || platformRole === 'admin'
    const role = isAdmin
      ? 'system_admin'
      : (currentUser?.role_at_ranch || platformRole || 'viewer')
    const permissions = currentUser?.permissions || ROLE_PERMISSIONS[role] || []
    const ranchTimezone = currentRanch?.timezone || 'UTC'
    // S6: owners (and viewer/external_auditor) are read-only in the app —
    // every entry button/form hides behind this flag. Owners keep exactly
    // two governance verbs (finance approval, grazing-week unlock), which
    // render independently of canWrite.
    const canWrite = !['owner', 'viewer', 'external_auditor'].includes(role)

    return {
      user: currentUser || effectiveMe || null,
      // The ranches this user is a member of (from /auth/me). Used by App's
      // RanchBoundary to auto-select a valid active ranch on first load.
      ranches: effectiveMe?.ranches || [],
      userId,
      role,
      canWrite,
      permissions,
      ranch: currentRanch || null,
      ranchTimezone,
      isLoading: meLoading || (!!userId && !!ranchId && userLoading),
      isAuthenticated: !!userId,
      hasPermission: (perm) => permissions.includes(perm),
      isRole: (...roles) => roles.includes(role),
      isAtLeast: (minRole) => {
        return ROLE_HIERARCHY.indexOf(role) >= ROLE_HIERARCHY.indexOf(minRole)
      },
      // The one way out: stops the refresh loop before clearing the cookie.
      logout,
    }
  }, [effectiveMe, currentUser, currentRanch, meLoading, userLoading, userId, ranchId, logout])

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export default AuthContext
